import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Chip } from "@bht/ui";
import { submitFeedback, type FeedbackCategory } from "../lib/api.js";
import { getCurrentItem } from "../lib/current-item.js";

/**
 * Always-available feedback affordance: a small speech-bubble button that lives
 * in the app header (top bar / mobile header) and opens a lightweight modal.
 *
 * This is the catch-all channel — distinct from the per-question FlagItemChip in
 * drill reveals. A learner can submit anything any time (most often: "I don't
 * trust this answer"), and we capture the current route + client so it triages
 * cleanly later. Keeps the doubt in-app instead of sending students to ChatGPT.
 */

const CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: "question", label: "About a question" },
  { value: "bug", label: "Something's broken" },
  { value: "idea", label: "Idea" },
  { value: "other", label: "Other" },
];

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        title="Send feedback"
        style={{
          background: "none",
          border: "none",
          padding: 4,
          cursor: "pointer",
          color: "var(--fg-2)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          // Glyph stays 18px; the hit box clears the 44px minimum around it.
          minWidth: 44,
          minHeight: 44,
          lineHeight: 0,
        }}
      >
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      </button>
      {open && <FeedbackModal onClose={() => setOpen(false)} />}
    </>
  );
}

function FeedbackModal({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const [category, setCategory] = useState<FeedbackCategory>("question");
  const [message, setMessage] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");

  async function send() {
    if (!message.trim() || state === "sending") return;
    setState("sending");
    try {
      const client =
        typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
          ? "pwa-desktop"
          : "pwa-mobile";
      // Attach the question on screen. A student writing "the answer being two
      // also makes sense" is reporting a specific item, and until 2026-09-07 that
      // arrived with item_id null, so identifying it meant matching their attempt
      // history on timestamps.
      await submitFeedback({
        category,
        message: message.trim(),
        path: pathname,
        client,
        item_id: getCurrentItem() ?? undefined,
      });
      setState("done");
      setTimeout(onClose, 1100);
    } catch {
      setState("error");
    }
  }

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden
        style={{ position: "fixed", inset: 0, zIndex: 60, background: "var(--bg-scrim, rgba(0,0,0,0.55))" }}
      />
      <div
        role="dialog"
        aria-label="Send feedback"
        style={{
          position: "fixed",
          zIndex: 61,
          left: "50%",
          top: "12%",
          transform: "translateX(-50%)",
          width: "min(440px, calc(100vw - 32px))",
          background: "var(--bg-1)",
          border: "1px solid var(--border-line)",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <h2 style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 18, fontWeight: 600, color: "var(--fg-0)" }}>
            Send feedback
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", color: "var(--fg-2)", cursor: "pointer", fontSize: 20, lineHeight: 1, minWidth: 44, minHeight: 44, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          >
            ×
          </button>
        </div>

        {state === "done" ? (
          <p style={{ margin: "8px 0 4px", color: "var(--fg-1)", fontFamily: "var(--font-sans)", fontSize: 15 }}>
            Thanks — we read every one of these.
          </p>
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {CATEGORIES.map((c) => (
                <Chip
                  key={c.value}
                  variant={category === c.value ? "accent" : "neutral"}
                  onClick={() => setCategory(c.value)}
                >
                  {c.label}
                </Chip>
              ))}
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What happened, or what's on your mind? If it's about a question, tell us which one and why."
              rows={4}
              maxLength={2000}
              autoFocus
              style={{
                width: "100%",
                resize: "vertical",
                background: "var(--bg-0)",
                color: "var(--fg-0)",
                border: "1px solid var(--border-line)",
                borderRadius: 8,
                padding: "10px 12px",
                fontFamily: "var(--font-sans)",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            />
            {state === "error" && (
              <p style={{ margin: 0, color: "var(--signal-red)", fontSize: 13 }}>
                Could not send. Check your connection and try again.
              </p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={send} disabled={!message.trim() || state === "sending"}>
                {state === "sending" ? "Sending…" : "Send"}
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
